import { FileText, Clock, HardDrive } from 'lucide-react'
import { StatsCard, QueueStatus, RecentTranscripts } from '@/components/dashboard'
import { useStats } from '@/hooks/useTranscripts'
import { formatDuration } from '@/lib/utils'

export function DashboardPage() {
  const { data: stats, isLoading } = useStats({ period: 'day' })

  const summary = stats?.summary

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Visão geral do sistema VCall
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total de Reuniões"
          value={isLoading ? '...' : (summary?.total_sessions || 0)}
          description="Todas as reuniões processadas"
          icon={FileText}
        />
        <StatsCard
          title="Horas de Áudio"
          value={isLoading ? '...' : `${(summary?.total_audio_processed_hours || 0).toFixed(1)}h`}
          description="Total processado"
          icon={Clock}
        />
        <StatsCard
          title="Tempo Médio"
          value={isLoading ? '...' : formatDuration(summary?.avg_audio_duration_sec || 0)}
          description="Duração média das reuniões"
          icon={Clock}
        />
        <StatsCard
          title="Dados Transcritos"
          value={isLoading ? '...' : `${((summary?.total_transcript_size_kb || 0) / 1024).toFixed(1)} MB`}
          description="Volume de texto gerado"
          icon={HardDrive}
        />
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-2">
        <QueueStatus />
        <RecentTranscripts />
      </div>

      {/* Reuniões por Dia Chart */}
      {stats?.periods && stats.periods.length > 0 && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-5 border-b">
            <h3 className="text-lg font-semibold">Reuniões por Dia</h3>
            <p className="text-sm text-muted-foreground mt-1">Últimos 7 dias de atividade</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {stats.periods.slice(0, 7).reverse().map((period) => {
                const maxCount = Math.max(...stats.periods.map(p => p.sessions_count), 1)
                const percentage = (period.sessions_count / maxCount) * 100
                const date = new Date(period.period + 'T12:00:00')
                const formattedDate = date.toLocaleDateString('pt-BR', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short'
                })

                return (
                  <div key={period.period} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground capitalize">
                        {formattedDate}
                      </span>
                      <span className="text-sm font-semibold text-primary">
                        {period.sessions_count} {period.sessions_count === 1 ? 'reunião' : 'reuniões'}
                      </span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500 ease-out group-hover:from-primary group-hover:to-primary"
                        style={{ width: `${Math.max(percentage, 2)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Summary footer */}
            <div className="mt-6 pt-4 border-t flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Total no período: <span className="font-semibold text-foreground">{stats.periods.slice(0, 7).reduce((acc, p) => acc + p.sessions_count, 0)} reuniões</span>
              </span>
              <span className="text-muted-foreground">
                Média: <span className="font-semibold text-foreground">{(stats.periods.slice(0, 7).reduce((acc, p) => acc + p.sessions_count, 0) / Math.min(7, stats.periods.length)).toFixed(1)}/dia</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
